import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Heart, Users, Calendar, MapPin, Home } from 'lucide-react';
import StarVolunteerCarousel from '../components/StarVolunteerCarousel';
import { stats } from '../mock';
import contentData from '../data/content.json';

const Volunteer = () => {
  const volunteerHeroImages = contentData.starVolunteers.slice(0, 4).map((volunteer) => volunteer.image);

  const volunteerAreas = [
    { title: 'Rescue Operations', description: 'Assist in animal rescue and transportation', icon: Heart },
    { title: 'Shelter Care', description: 'Feed, clean, and care for animals at our facility', icon: Home },
    { title: 'Event Management', description: 'Help organize adoption drives and awareness campaigns', icon: Calendar },
    { title: 'Social Media', description: 'Create content and manage our online presence', icon: Users },
    { title: 'Fundraising', description: 'Assist with donor outreach and fundraising events', icon: Users },
    { title: 'Medical Support', description: 'Assist veterinarians during treatment (if qualified)', icon: Heart }
  ];

  const photogallery = [
    {
      id: 1,
      image: '/volunteer/1.jpeg'
    },
    {
      id: 2,
      image: '/volunteer/2.jpeg'
    },
    {
      id: 3,
      image: '/volunteer/3.jpeg'
    },
    {
      id: 4,
      image: '/volunteer/4.jpeg'
    },
    {
      id: 5,
      image: '/volunteer/5.jpeg'
    },
    {
      id: 6,
      image: '/volunteer/6.jpeg'
    },
    {
      id: 7,
      image: '/volunteer/7.jpeg'
    },
    {
      id: 8,
      image: '/volunteer/8.jpeg'
    },
    {
      id: 9,
      image: '/volunteer/9.jpeg'
    },
    {
      id: 10,
      image: '/volunteer/10.jpeg'
    },
    {
      id: 11,
      image: '/volunteer/11.jpeg'
    }

  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-0.5 md:grid-cols-4 md:grid-rows-1">
          {volunteerHeroImages.map((image, index) => (
            <div key={index} className="relative overflow-hidden">
              <img
                src={image}
                alt="Volunteer story"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-6">
            Volunteer With Us
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-8">
            Join our team of passionate volunteers making a real difference in animal welfare.
          </p>
          <div className="flex items-center justify-center space-x-2 text-lg">
            <Users className="w-6 h-6" />
            <span>{stats.volunteersActive} active volunteers across 5 cities</span>
          </div>
        </div>
      </section>

      {/* Why Volunteer */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Why Volunteer?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Volunteering is one of the most rewarding ways to contribute to animal welfare
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <Heart className="w-12 h-12 text-warm-amber mx-auto mb-4" fill="currentColor" />
                <h3 className="text-xl font-bold text-forest-green mb-3">Make Real Impact</h3>
                <p className="text-gray-700">Directly save lives and improve animal welfare in your community</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-warm-amber mx-auto mb-4" />
                <h3 className="text-xl font-bold text-forest-green mb-3">Join a Community</h3>
                <p className="text-gray-700">Connect with like-minded animal lovers and build lasting friendships</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-warm-amber mx-auto mb-4" />
                <h3 className="text-xl font-bold text-forest-green mb-3">Flexible Schedule</h3>
                <p className="text-gray-700">Volunteer according to your availability and preferences</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Star Volunteer Carousel */}
      <section className="py-20 bg-gradient-to-b from-sand-beige to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Star Volunteers of the Month
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Celebrating exceptional volunteers who go above and beyond to make a difference
            </p>
          </div>
          <StarVolunteerCarousel />
        </div>
      </section>

      {/* Volunteer Areas */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Areas You Can Volunteer In
            </h2>
            <p className="text-xl text-gray-700">Choose the area that matches your skills and interests</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {volunteerAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <Card key={index} className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <Icon className="w-10 h-10 text-warm-amber mb-4" />
                    <h3 className="text-lg font-bold text-forest-green mb-2">{area.title}</h3>
                    <p className="text-gray-700 text-sm">{area.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Volunteer Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold font-heading text-forest-green mb-8 text-center">
                  Volunteer Application
                </h2>

                <div className="w-full">
                  <iframe
                    src="https://docs.google.com/forms/d/e/1FAIpQLScGjt-DFRjoe5RP5VJ8j7dbIvhm15YrEFnWqwKXg8e0e-8Pig/viewform?viewform?embedded=true"
                    width="100%"
                    height="1200"
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    title="Volunteer Application Form"
                  >
                    Loading…
                  </iframe>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Our Volunteers in Action
            </h2>
            <p className="text-xl text-gray-700">See the amazing work our volunteers do every day</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {photogallery.map((item, index) => (
              <div key={item.id} className="relative h-64 rounded-lg overflow-hidden shadow-lg group">
                <img
                  src={item.image}
                  alt={`Volunteer activity ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest-green/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Volunteer;